import { styled } from '@linaria/react';
import { Loader } from '@mantine/core';
import type { ReactNode } from 'react';
import { Icon } from '../../../../../ui-library/icon/icon';
import { AgentViewHeader } from '../../agent-view-header';
import { getFloraSettingsNavItem } from '../agent-settings.constants';
import type { FloraSettingsSection } from '../agent-settings.type';
import { SettingsSectionIcon } from '../settings-section-icon';

interface SettingsSectionShellProps {
  /** The section shown; its menu entry supplies the heading and icon tile. */
  section: FloraSettingsSection;
  subtitle?: string;
  /** Trailing header content, usually the save status. */
  headerRight?: ReactNode;
  isLoading?: boolean;
  /** Message shown instead of the content when loading failed. */
  errorMessage?: string | null;
  children?: ReactNode;
}

/**
 * Renders one settings section: its page heading (with the section's icon
 * tile), then a loader, an error message, or the section's cards stacked with
 * even spacing.
 *
 * @param {SettingsSectionShellProps} props - The heading, load state, and cards
 * @returns {JSX.Element} The section page
 *
 * @example
 * <SettingsSectionShell section={FloraSettingsSection.Tools} headerRight={<SaveStatus state={saveState} />} isLoading={isLoading}>
 *   <SettingsCard>…</SettingsCard>
 * </SettingsSectionShell>
 */
export function SettingsSectionShell({
  section,
  subtitle,
  headerRight,
  isLoading = false,
  errorMessage = null,
  children,
}: SettingsSectionShellProps) {
  return (
    <div>
      <AgentViewHeader
        variant="settings-page"
        title={getFloraSettingsNavItem(section).label}
        subtitle={subtitle}
        rightSlot={headerRight}
        leading={<SettingsSectionIcon section={section} />}
      />
      {isLoading ? (
        <StateBox>
          <Loader size="sm" color="var(--secondary-base)" />
        </StateBox>
      ) : errorMessage ? (
        <StateBox role="alert">
          <IconSlot aria-hidden="true">
            <Icon name="error" color="var(--ink-light)" size={32} />
          </IconSlot>
          <StateText>{errorMessage}</StateText>
        </StateBox>
      ) : (
        <Stack>{children}</Stack>
      )}
    </div>
  );
}

/**
 * Stacks a section's cards with the standard settings spacing.
 */
export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const StateBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 200px;
`;

const StateText = styled.p`
  margin: 0;
  font-size: var(--font-size-regular);
  color: var(--ink-light);
`;

const IconSlot = styled.span`
  display: inline-flex;
`;

export type { SettingsSectionShellProps };
